import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Check, ArrowRight, Clock, Coins, Sparkles, Plus, Loader2, AlertCircle, X, Mail, Copy, Globe, MapPin } from 'lucide-react';
import DraggablePopularTag from './DraggablePopularTag';
import { useLanguage, translations } from '../i18nContext';
import {
  purchaseMembership,
  purchaseDurationPackage,
  confirmMembershipPurchase,
  confirmDurationPackagePurchase,
  getMembershipInfo,
  getPaymentRegion,
  PaymentRegionInfo,
} from '../services/membership';
import { MembershipInfo } from '../types';
import { copyToClipboard } from '../utils/clipboard';

interface PricingProps {
  onClose?: () => void;
}

export default function Pricing({ onClose }: PricingProps) {
  const [isAnnual, setIsAnnual] = useState(true);
  const { t, language } = useLanguage();

  // 购买状态
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState<string | null>(null);
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // 地区检测弹窗状态
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [regionInfo, setRegionInfo] = useState<PaymentRegionInfo | null>(null);
  const [regionLoading, setRegionLoading] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<{ type: 'membership' | 'duration'; value: string } | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);

  // 二维码弹窗状态
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [qrOrderNo, setQrOrderNo] = useState<string | null>(null);
  const [qrPaymentType, setQrPaymentType] = useState<string | null>(null);

  // 价格地区状态（根据IP检测，独立于UI语言）
  const [paymentRegion, setPaymentRegion] = useState<string>('CN');

  const isCofounder = membershipInfo?.isCofounder === true;
  const cofounderDiscount = membershipInfo?.cofounderDiscount || 0;
  const discountMultiplier = isCofounder ? (100 - cofounderDiscount) / 100 : 1;

  // 计算Co-founder折扣价
  const applyDiscount = (price: number | string): string => {
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num) || !isCofounder) return String(price);
    return (num * discountMultiplier).toFixed(0);
  };

  // 根据IP地区选择价格和币种（独立于UI语言）
  const regionPricing = paymentRegion === 'OVERSEAS' ? translations.en.pricing : translations.zh.pricing;
  const regionPrices = regionPricing.prices;
  const regionCurrency = regionPricing.currencySymbol;

  // 加载会员信息
  useEffect(() => {
    loadMembershipInfo();
    // 检测IP地区，决定价格币种
    getPaymentRegion().then(info => {
      setPaymentRegion(info.region);
    }).catch(() => {});
  }, []);

  const loadMembershipInfo = async () => {
    try {
      const info = await getMembershipInfo();
      setMembershipInfo(info);
    } catch (err) {
      // 未登录或获取失败，忽略
    }
  };

  // 点击购买时先检测地区
  const handlePurchaseClick = async (type: 'membership' | 'duration', value: string) => {
    setPendingPurchase({ type, value });
    setRegionLoading(true);
    setShowRegionModal(true);
    try {
      const info = await getPaymentRegion();
      setRegionInfo(info);
    } catch {
      setRegionInfo(null);
    } finally {
      setRegionLoading(false);
    }
  };

  // 弹窗确认后执行真正的购买
  const handleRegionConfirm = () => {
    if (!selectedPaymentMethod) return;
    setShowRegionModal(false);
    if (!pendingPurchase) return;
    const method = selectedPaymentMethod;
    setSelectedPaymentMethod(null);
    if (pendingPurchase.type === 'membership') {
      handlePurchaseMembership(pendingPurchase.value as 'pro' | 'team', method);
    } else {
      handlePurchaseDurationPackage(pendingPurchase.value, method);
    }
    setPendingPurchase(null);
  };

  // 购买会员
  const handlePurchaseMembership = async (membershipType: 'pro' | 'team', paymentMethod: string) => {
    setPurchasing(membershipType);
    setPurchaseError(null);
    setPurchaseSuccess(null);

    try {
      const months = isAnnual ? 12 : 1;
      const response = await purchaseMembership({
        membershipType,
        months,
        isAnnual,
        paymentMethod,
      });

      if (response.codeUrl) {
        setQrCodeUrl(response.codeUrl);
        setQrOrderNo(response.orderNo);
        setQrPaymentType(response.paymentType || null);
        setShowQrModal(true);
      } else if (response.payForm) {
        // 支付宝电脑网站支付：打开新窗口提交表单
        const div = document.createElement('div');
        div.innerHTML = response.payForm;
        document.body.appendChild(div);
        const form = div.querySelector('form');
        if (form) {
          form.setAttribute('target', '_blank');
          form.submit();
        }
        document.body.removeChild(div);
        setPurchaseSuccess(
          language === 'zh'
            ? '已跳转支付宝支付页面，请在新窗口完成支付'
            : 'Redirected to Alipay payment page, please complete payment in the new window'
        );
        await loadMembershipInfo();
      } else {
        await confirmMembershipPurchase(response.orderNo);
        setPurchaseSuccess(
          language === 'zh'
            ? `购买成功！已开通${membershipType === 'pro' ? 'Pro' : 'Team'}会员`
            : `Purchase successful! ${membershipType === 'pro' ? 'Pro' : 'Team'} membership activated`
        );
        await loadMembershipInfo();
      }
    } catch (err: any) {
      setPurchaseError(err.message || (language === 'zh' ? '购买失败' : 'Purchase failed'));
    } finally {
      setPurchasing(null);
    }
  };

  // 购买时长包
  const handlePurchaseDurationPackage = async (packageCode: string, paymentMethod: string) => {
    setPurchasing(packageCode);
    setPurchaseError(null);
    setPurchaseSuccess(null);

    try {
      const response = await purchaseDurationPackage({ packageCode, paymentMethod });

      if (response.codeUrl) {
        setQrCodeUrl(response.codeUrl);
        setQrOrderNo(response.orderNo);
        setQrPaymentType(response.paymentType || null);
        setShowQrModal(true);
      } else if (response.payForm) {
        const div = document.createElement('div');
        div.innerHTML = response.payForm;
        document.body.appendChild(div);
        const form = div.querySelector('form');
        if (form) {
          form.setAttribute('target', '_blank');
          form.submit();
        }
        document.body.removeChild(div);
        setPurchaseSuccess(
          language === 'zh'
            ? '已跳转支付宝支付页面，请在新窗口完成支付'
            : 'Redirected to Alipay payment page, please complete payment in the new window'
        );
        await loadMembershipInfo();
      } else {
        await confirmDurationPackagePurchase(response.orderNo);
        setPurchaseSuccess(
          language === 'zh'
            ? `购买成功！已添加时长包`
            : `Purchase successful! Duration package added`
        );
        await loadMembershipInfo();
      }
    } catch (err: any) {
      setPurchaseError(err.message || (language === 'zh' ? '购买失败' : 'Purchase failed'));
    } finally {
      setPurchasing(null);
    }
  };

  // 获取按钮文本
  const getButtonText = (type: string) => {
    if (purchasing === type) {
      return language === 'zh' ? '处理中...' : 'Processing...';
    }
    if (membershipInfo?.userLevel === type) {
      return language === 'zh' ? '当前方案' : 'Current Plan';
    }
    return null;
  };

  // Draggable tag uses DraggablePopularTag component

  return (
    <div className="w-full pt-12 pb-24 bg-surface min-h-screen relative overflow-hidden font-sans text-brand-900">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-brand-200/20 rounded-full blur-[120px] pointer-events-none opacity-60"></div>
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-prestige-300/10 rounded-full blur-[120px] pointer-events-none opacity-60"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">

        {/* 购买状态提示 */}
        {(purchaseError || purchaseSuccess) && (
          <div className="fixed top-4 right-4 z-50 max-w-md animate-in slide-in-from-top-2 duration-300">
            {purchaseError && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 shadow-lg flex items-start gap-3">
                <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-red-700">{language === 'zh' ? '购买失败' : 'Purchase Failed'}</div>
                  <div className="text-xs text-red-600 mt-1">{purchaseError}</div>
                </div>
                <button onClick={() => setPurchaseError(null)} className="text-red-400 hover:text-red-600">×</button>
              </div>
            )}
            {purchaseSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 shadow-lg flex items-start gap-3">
                <Check size={20} className="text-green-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-green-700">{language === 'zh' ? '购买成功' : 'Purchase Successful'}</div>
                  <div className="text-xs text-green-600 mt-1">{purchaseSuccess}</div>
                </div>
                <button onClick={() => setPurchaseSuccess(null)} className="text-green-400 hover:text-green-600">×</button>
              </div>
            )}
          </div>
        )}

        {/* Header Section - Better Proportions */}
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-brand-900 mb-6 tracking-tight">
            {t.pricing.title}
          </h2>
          
          {/* Refined Premium Switcher */}
          <div className="flex items-center justify-center">
             <div 
                className="bg-surface-border/50 p-1.5 rounded-full border border-surface-border inline-flex relative cursor-pointer min-w-[400px] h-14"
                onClick={() => setIsAnnual(!isAnnual)}
             >
                 {/* Animated Slide Background */}
                 <div 
                    className={`absolute top-1.5 bottom-1.5 rounded-full transition-all duration-500 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] bg-brand-900 shadow-lg
                    ${isAnnual ? 'left-[calc(35%+6px)] right-1.5' : 'left-1.5 right-[calc(65%+6px)]'}`}
                 ></div>
                 
                 <div className={`w-[35%] flex items-center justify-center text-[11px] font-bold relative z-10 transition-colors duration-300 uppercase tracking-widest ${!isAnnual ? 'text-white' : 'text-ink-dark'}`}>
                     {t.pricing.monthly}
                 </div>

                 <div className={`w-[65%] flex items-center justify-center gap-3 text-[11px] font-bold relative z-10 transition-colors duration-300 uppercase tracking-widest ${isAnnual ? 'text-white' : 'text-ink-dark'}`}>
                     {t.pricing.yearly}
                     {/* 
                        REDESIGNED BADGE: 
                        Ensured the hint is "obvious" even when unselected.
                        Selected: Solid High-Vis Green with glow.
                        Unselected: High-contrast Outlined High-Vis Green.
                     */}
                     <span className={`text-[10px] px-3 py-1 rounded-lg font-black transition-all duration-500 border-2 shadow-sm 
                        ${isAnnual 
                            ? 'bg-brand-300 text-brand-900 border-white/20 shadow-[0_0_20px_rgba(190,242,100,0.4)] scale-105' 
                            : 'bg-white text-brand-600 border-brand-300'}`}>
                        -20% OFF
                     </span>
                 </div>
             </div>
          </div>
        </div>

        {/* Pricing Cards Grid - Larger fonts and better internal spacing */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
            
            {/* 1. Essential (Free) */}
            <div className="group p-10 rounded-pricing-lg bg-white border border-surface-border flex flex-col transition-all duration-500 hover:shadow-card-hover">
                <div className="h-[110px] flex flex-col justify-start">
                    <div className="text-[11px] font-bold text-ink-muted uppercase tracking-[0.2em] mb-3">ESSENTIAL</div>
                    <h3 className="text-4xl font-serif font-bold text-brand-900 mb-2">{t.pricing.plans.basic.name}</h3>
                    <p className="text-ink-secondary text-sm leading-relaxed opacity-60 font-medium">{t.pricing.plans.basic.desc}</p>
                </div>
                
                <div className="h-[90px] mb-4 flex flex-col justify-start">
                    <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-serif text-brand-900 opacity-40">{regionCurrency}</span>
                        <span className="text-6xl font-serif text-brand-900 font-bold">{regionPrices.free}</span>
                        <span className="text-ink-muted text-xs font-bold uppercase tracking-widest ml-1">/mo</span>
                    </div>
                </div>

                <div className="flex-1 mb-8">
                    <ul className="space-y-4">
                        {t.pricing.features.basic.map((feature, i) => (
                            <li key={i} className="flex gap-4 items-start text-sm font-medium text-ink-secondary">
                                <div className="mt-0.5 w-5 h-5 rounded-full bg-surface flex items-center justify-center text-ink-muted group-hover:bg-surface-border group-hover:text-brand-900 transition-colors shrink-0">
                                    <Check size={12} strokeWidth={3} />
                                </div>
                                <span className="line-clamp-2">{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-auto relative z-10 space-y-4">
                  <button className="w-full h-16 text-brand-900 font-bold text-sm bg-surface rounded-2xl border border-surface-border hover:bg-white hover:shadow-md transition-all active:scale-[0.98]">
                      {t.pricing.getStarted}
                  </button>
                  <div className="h-[15px] opacity-0 pointer-events-none select-none">Placeholder</div>
                </div>
            </div>

            {/* 2. Professional (Pro) - Enhanced Visibility with Glow */}
            <div className="relative p-10 rounded-pricing-lg bg-brand-50 border border-brand-300/40 shadow-[0_0_60px_-15px_rgba(132,204,22,0.5)] ring-2 ring-brand-400/20 flex flex-col transition-all duration-500 z-10 group/pro overflow-visible">
                
                {/* REFINED "POPULAR" TAG - Draggable */}
                <DraggablePopularTag label={t.pricing.mostPopular} className="top-8 right-12" />

                <div className="h-[110px] flex flex-col justify-start">
                    <div className="text-[11px] font-bold text-brand-700 uppercase tracking-[0.2em] mb-3">PROFESSIONAL</div>
                    <h3 className="text-4xl font-serif font-bold text-brand-900 mb-2">{t.pricing.plans.pro.name}</h3>
                    <p className="text-brand-700 text-sm leading-relaxed font-medium opacity-80 max-w-[85%]">{t.pricing.plans.pro.desc}</p>
                </div>
                
                <div className="h-[90px] mb-4 flex flex-col justify-start text-brand-900">
                    <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-serif font-bold opacity-40">{regionCurrency}</span>
                        <span className="text-6xl font-serif font-bold tracking-tighter transition-all duration-500">{isCofounder ? applyDiscount(isAnnual ? regionPrices.pro.yearly : regionPrices.pro.monthly) : (isAnnual ? regionPrices.pro.yearly : regionPrices.pro.monthly)}</span>
                        <span className="text-brand-700 text-xs font-bold uppercase tracking-widest ml-1">/mo</span>
                    </div>
                    {isCofounder && (
                        <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-sm text-ink-muted line-through font-mono">{regionCurrency}{isAnnual ? regionPrices.pro.yearly : regionPrices.pro.monthly}</span>
                            {isAnnual && <span className="text-sm text-ink-muted line-through font-mono">{regionCurrency}{regionPrices.pro.monthly}</span>}
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-black uppercase tracking-wider border border-amber-200/60">Co-founder -{cofounderDiscount}%</span>
                        </div>
                    )}
                    {!isCofounder && isAnnual && (
                        <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-sm text-ink-muted line-through font-mono">{regionCurrency}{regionPrices.pro.monthly}</span>
                        </div>
                    )}
                    {isAnnual && (
                        <div className="mt-1 text-[11px] font-bold text-brand-600 flex items-center gap-1.5 h-[16px]">
                             <Sparkles size={12} fill="currentColor" />
                             {t.pricing.annualSaveTag}
                        </div>
                    )}
                    <div className="text-[10px] text-ink-muted font-bold mt-1 opacity-80 italic">
                        {t.pricing.priceAnchor}
                    </div>
                </div>

                <div className="flex-1 mb-8">
                    <ul className="space-y-4">
                         {t.pricing.features.pro.map((feature, i) => (
                            <li key={i} className="flex gap-4 items-start text-sm font-bold text-brand-900">
                                <div className="mt-0.5 w-6 h-6 rounded-full bg-white flex items-center justify-center text-brand-500 shadow-sm shrink-0">
                                    <Check size={14} strokeWidth={3} />
                                </div>
                                <span className="line-clamp-2">{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-auto relative z-10 space-y-4">
                  <button
                    onClick={() => handlePurchaseClick('membership', 'pro')}
                    disabled={purchasing === 'pro'}
                    className={`w-full relative h-16 rounded-2xl font-bold text-base transition-all overflow-hidden group/btn ${
                      'bg-brand-900 text-white shadow-[0_12px_24px_-8px_rgba(26,46,5,0.4)] hover:shadow-[0_20px_40px_-8px_rgba(26,46,5,0.5)] hover:scale-[1.02] active:scale-[0.98]'
                    }`}
                  >
                      <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                      <span className="relative z-10 flex items-center justify-center gap-3">
                          {purchasing === 'pro' ? (
                            <>
                              <Loader2 size={18} className="animate-spin" />
                              {language === 'zh' ? '处理中...' : 'Processing...'}
                            </>
                          ) : membershipInfo?.userLevel === 'pro' ? (
                            <>
                              {language === 'zh' ? '续费' : 'Renew'}
                              <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-transform stroke-[2.5]" />
                            </>
                          ) : (
                            <>
                              {t.pricing.startFree}
                              <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-transform stroke-[2.5]" />
                            </>
                          )}
                      </span>
                  </button>
                  <div className="h-[15px] opacity-0 pointer-events-none select-none">Placeholder</div>
                </div>
            </div>

            {/* 3. Executive (Team) */}
            <div className="group p-10 rounded-pricing-lg bg-prestige-900 text-surface-light border border-ink-dark shadow-2xl flex flex-col relative overflow-hidden transition-all duration-500 hover:shadow-[0_40px_80px_-20px_rgba(62,52,48,0.3)]">
                <div className="absolute top-0 right-0 w-64 h-64 bg-prestige-300/5 rounded-bl-[160px] pointer-events-none"></div>
                <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>
                
                <div className="h-[110px] flex flex-col justify-start relative z-10">
                    <div className="text-[11px] font-bold text-prestige-300 uppercase tracking-[0.2em] mb-3">EXECUTIVE</div>
                    <h3 className="text-4xl font-serif font-bold tracking-tight mb-2">{t.pricing.plans.team.name}</h3>
                    <p className="text-white/40 text-sm leading-relaxed font-medium">{t.pricing.plans.team.desc}</p>
                </div>
                
                <div className="h-[90px] mb-4 relative z-10 flex flex-col justify-center">
                    <div className="text-xl font-serif font-bold text-prestige-300">
                        {language === 'zh' ? '专属定制方案' : 'Custom Enterprise Plan'}
                    </div>
                    <p className="text-xs text-white/40 mt-1">
                        {language === 'zh' ? '根据团队需求量身定制' : 'Tailored to your team needs'}
                    </p>
                </div>

                <div className="flex-1 mb-8 relative z-10">
                    <ul className="space-y-4">
                        {t.pricing.features.team.map((feature, i) => (
                            <li key={i} className="flex gap-4 items-start text-sm font-medium text-white/70">
                                <div className="mt-0.5 w-5 h-5 flex items-center justify-center shrink-0">
                                    <Check size={16} className="text-prestige-300 stroke-[3]" />
                                </div>
                                <span className="line-clamp-2">{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-auto relative z-10 space-y-4">
                    <button
                      onClick={() => setShowContactModal(true)}
                      disabled={membershipInfo?.userLevel === 'team'}
                      className={`w-full h-16 rounded-2xl font-bold text-base transition-all shadow-xl active:scale-[0.98] group/team ${
                        membershipInfo?.userLevel === 'team'
                          ? 'bg-ink-dark text-white/50 cursor-default'
                          : 'bg-white text-prestige-900 hover:bg-prestige-300'
                      }`}
                    >
                        <span className="flex items-center justify-center gap-3">
                            {membershipInfo?.userLevel === 'team' ? (
                              language === 'zh' ? '当前方案' : 'Current Plan'
                            ) : (
                              <>
                                {t.pricing.contactSales}
                                <ArrowRight size={18} className="group-hover/team:translate-x-1 transition-transform stroke-[2.5]" />
                              </>
                            )}
                        </span>
                    </button>
                    <div className="text-[10px] text-white/20 text-center font-bold uppercase tracking-widest truncate">
                        {t.pricing.enterpriseOnboarding}
                    </div>
                </div>
            </div>
        </div>

        {/* Add-on Section - New Tiered Add-on UI */}
        <div className="mt-16 max-w-6xl mx-auto">
            <div className="text-center mb-8">
                <h3 className="text-2xl font-serif font-bold text-brand-900 mb-2">{t.pricing.addons.title}</h3>
                <p className="text-sm text-ink-secondary font-medium">{t.pricing.addons.desc}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* Add-on 1: Single */}
                <div className="bg-white p-6 rounded-modal border border-surface-border hover:shadow-md transition-all group flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-surface rounded-2xl flex items-center justify-center text-ink-muted group-hover:bg-surface-border group-hover:text-brand-900 transition-colors">
                            <Clock size={24} />
                        </div>
                        <div>
                            <div className="text-xs font-bold text-ink-muted uppercase tracking-wider mb-0.5">{t.pricing.addons.single}{isCofounder && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-black uppercase tracking-wider border border-amber-200/60 ml-2">Co-founder -{cofounderDiscount}%</span>}</div>
                            <div className="text-lg font-bold text-brand-900">{regionCurrency}{isCofounder ? applyDiscount(regionPrices.addons.single) : regionPrices.addons.single}{isCofounder && <span className="text-xs text-ink-muted line-through ml-1.5">{regionCurrency}{regionPrices.addons.single}</span>}</div>
                        </div>
                    </div>
                    <button
                      onClick={() => handlePurchaseClick('duration', '60min')}
                      disabled={purchasing === '60min'}
                      className="p-2.5 rounded-xl border border-surface-border hover:bg-brand-900 hover:text-white transition-all text-brand-900 disabled:opacity-50"
                    >
                        {purchasing === '60min' ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} strokeWidth={3} />}
                    </button>
                </div>

                {/* Add-on 2: Value Pack (Popular) - Enhanced Visibility with Glow */}
                <div className="bg-brand-50 p-6 rounded-modal border border-brand-300/40 shadow-[0_0_35px_-10px_rgba(132,204,22,0.45)] ring-1 ring-brand-400/10 relative group flex items-center justify-between overflow-hidden">
                    <div className="absolute top-0 right-8 px-2 py-0.5 bg-brand-900 text-brand-300 text-[9px] font-black uppercase tracking-widest rounded-b-md">
                        {t.pricing.addons.popular}
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-brand-500 shadow-sm">
                            <Coins size={24} />
                        </div>
                        <div>
                            <div className="text-xs font-bold text-brand-700 uppercase tracking-wider mb-0.5">{t.pricing.addons.value}{isCofounder && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-black uppercase tracking-wider border border-amber-200/60 ml-2">Co-founder -{cofounderDiscount}%</span>}</div>
                            <div className="text-lg font-bold text-brand-900">{regionCurrency}{isCofounder ? applyDiscount(regionPrices.addons.value.price) : regionPrices.addons.value.price} {isCofounder && <span className="text-xs text-ink-muted line-through">{regionCurrency}{regionPrices.addons.value.price}</span>} <span className="text-xs text-ink-muted line-through">{regionCurrency}{regionPrices.addons.value.originalPrice}</span> <span className="text-[10px] text-brand-700 font-bold opacity-60">{regionPrices.addons.value.detail}</span></div>
                        </div>
                    </div>
                    <button
                      onClick={() => handlePurchaseClick('duration', '600min')}
                      disabled={purchasing === '600min'}
                      className="p-2.5 rounded-xl bg-brand-900 text-white shadow-md hover:scale-110 active:scale-95 transition-all disabled:opacity-50"
                    >
                        {purchasing === '600min' ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} strokeWidth={3} />}
                    </button>
                </div>

                {/* Add-on 3: Business Pack */}
                <div className="bg-prestige-900 p-6 rounded-modal border border-ink-dark group flex items-center justify-between shadow-xl">
                    <div className="flex items-center gap-4 text-white">
                        <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-prestige-300 backdrop-blur-sm border border-white/10">
                            <Clock size={24} />
                        </div>
                        <div>
                            <div className="text-xs font-bold text-prestige-300/60 uppercase tracking-wider mb-0.5">{t.pricing.addons.business}{isCofounder && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-900/30 text-prestige-300 font-black uppercase tracking-wider border border-prestige-300/30 ml-2">Co-founder -{cofounderDiscount}%</span>}</div>
                            <div className="text-lg font-bold text-white">{regionCurrency}{isCofounder ? applyDiscount(regionPrices.addons.business.price) : regionPrices.addons.business.price} {isCofounder && <span className="text-xs text-white/30 line-through">{regionCurrency}{regionPrices.addons.business.price}</span>} <span className="text-xs text-white/30 line-through">{regionCurrency}{regionPrices.addons.business.originalPrice}</span> <span className="text-[10px] text-white/40 font-bold">{regionPrices.addons.business.detail}</span></div>
                        </div>
                    </div>
                    <button
                      onClick={() => handlePurchaseClick('duration', '3000min')}
                      disabled={purchasing === '3000min'}
                      className="p-2.5 rounded-xl bg-prestige-300 text-prestige-900 shadow-md hover:scale-110 active:scale-95 transition-all disabled:opacity-50"
                    >
                        {purchasing === '3000min' ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} strokeWidth={3} />}
                    </button>
                </div>

            </div>
        </div>

        {/* Confidence Banner - Adjusted Spacing */}
        <div className="mt-16 max-w-4xl mx-auto">
            <div className="p-10 rounded-pricing bg-white border border-surface-border shadow-sm flex flex-col md:flex-row items-center gap-12 group hover:shadow-md transition-shadow">
                <div className="flex-1">
                    <h4 className="text-2xl font-serif font-bold text-brand-900 mb-2">{t.pricing.trustTitle}</h4>
                    <p className="text-sm text-ink-secondary font-medium leading-relaxed">
                        {t.pricing.trustDesc}
                    </p>
                </div>
                <div className="flex gap-12 items-center shrink-0 border-l border-surface-border pl-12 md:pl-16">
                    <div className="text-center">
                        <div className="text-3xl font-serif font-bold text-brand-900">99.9%</div>
                        <div className="text-[10px] font-bold text-ink-muted uppercase tracking-widest mt-1">Accuracy</div>
                    </div>
                    <div className="text-center">
                        <div className="text-3xl font-serif font-bold text-brand-900">AES-256</div>
                        <div className="text-[10px] font-bold text-ink-muted uppercase tracking-widest mt-1">Security</div>
                    </div>
                </div>
            </div>
        </div>

      </div>

      {/* Team 联系我们弹窗 */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowContactModal(false)}
          ></div>
          <div className="relative w-full max-w-md bg-surface-light rounded-modal shadow-2xl border border-surface-border overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-prestige-300/10 rounded-bl-[120px] pointer-events-none"></div>

            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-6 right-6 z-20 p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>

            <div className="p-10 relative z-10">
              <div className="w-16 h-16 bg-prestige-900 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Mail size={28} className="text-prestige-300" />
              </div>

              <h3 className="text-2xl font-serif font-bold text-brand-900 text-center mb-2">
                {language === 'zh' ? '联系我们' : 'Contact Us'}
              </h3>
              <p className="text-sm text-ink-secondary text-center mb-8 leading-relaxed">
                {language === 'zh'
                  ? 'Team 方案需要专属定制，请通过邮件联系我们，我们将为您提供专属服务。'
                  : 'Team plans require custom setup. Please contact us via email for dedicated service.'}
              </p>

              <div className="bg-surface rounded-2xl border border-surface-border p-5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-white rounded-xl border border-surface-border flex items-center justify-center shrink-0 shadow-sm">
                    <Mail size={18} className="text-ink-secondary" />
                  </div>
                  <span className="text-sm font-mono font-bold text-brand-900 truncate">
                    support@minutes-ai.com
                  </span>
                </div>
                <button
                  onClick={() => {
                    copyToClipboard('support@minutes-ai.com');
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className={`shrink-0 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                    copied
                      ? 'bg-brand-500 text-white'
                      : 'bg-brand-900 text-white hover:bg-brand-800'
                  }`}
                >
                  {copied
                    ? (language === 'zh' ? '已复制' : 'Copied')
                    : (language === 'zh' ? '复制' : 'Copy')}
                </button>
              </div>

              <button
                onClick={() => setShowContactModal(false)}
                className="w-full mt-6 py-3 rounded-xl text-sm font-bold text-ink-secondary bg-white border border-surface-border hover:bg-surface transition-all"
              >
                {language === 'zh' ? '关闭' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 地区检测 + 支付方式选择弹窗 */}
      {showRegionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { setShowRegionModal(false); setPendingPurchase(null); setSelectedPaymentMethod(null); }}
          ></div>
          <div className="relative w-full max-w-md bg-surface-light rounded-modal shadow-2xl border border-surface-border overflow-hidden animate-in zoom-in-95 duration-300">
            <button
              onClick={() => { setShowRegionModal(false); setPendingPurchase(null); setSelectedPaymentMethod(null); }}
              className="absolute top-6 right-6 z-20 p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>

            <div className="p-10">
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Globe size={28} className="text-brand-700" />
              </div>

              <h3 className="text-2xl font-serif font-bold text-brand-900 text-center mb-2">
                {language === 'zh' ? '选择支付方式' : 'Choose Payment Method'}
              </h3>

              {regionLoading ? (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 size={32} className="animate-spin text-brand-700" />
                  <p className="text-sm text-ink-secondary">{language === 'zh' ? '正在检测您的地区...' : 'Detecting your region...'}</p>
                </div>
              ) : regionInfo ? (
                <div className="space-y-4">
                  <div className="bg-surface rounded-2xl border border-surface-border p-4">
                    <div className="flex items-center gap-2 text-xs text-ink-secondary">
                      <MapPin size={14} className="text-brand-700" />
                      <span>{regionInfo.region === 'CN' ? (language === 'zh' ? '国内' : 'Mainland China') : (language === 'zh' ? '国外' : 'Overseas')}</span>
                      {regionInfo.city && <span>· {regionInfo.city}</span>}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-xs font-bold text-brand-700 mb-1">
                      {language === 'zh' ? '请选择支付方式' : 'Select payment method'}
                    </div>
                    {regionInfo.availablePaymentMethods.filter(m => m === 'wechat' || m === 'alipay').map((m) => (
                      <button
                        key={m}
                        onClick={() => setSelectedPaymentMethod(m)}
                        className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${
                          selectedPaymentMethod === m
                            ? 'border-brand-700 bg-brand-50'
                            : 'border-surface-border bg-white hover:border-brand-200'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold ${
                          m === 'wechat' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          {m === 'wechat' ? '微' : '支'}
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-bold text-brand-900">
                            {m === 'wechat' ? (language === 'zh' ? '微信支付' : 'WeChat Pay') : (language === 'zh' ? '支付宝' : 'Alipay')}
                          </div>
                          <div className="text-xs text-ink-secondary">
                            {m === 'wechat' ? (language === 'zh' ? '使用微信扫码支付' : 'Scan QR code with WeChat') : (language === 'zh' ? '跳转支付宝页面支付' : 'Redirect to Alipay payment page')}
                          </div>
                        </div>
                        <div className="ml-auto">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedPaymentMethod === m ? 'border-brand-700 bg-brand-700' : 'border-ink-border'
                          }`}>
                            {selectedPaymentMethod === m && <Check size={12} className="text-white" />}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-sm text-ink-secondary">
                  {language === 'zh' ? '地区检测失败，将使用默认设置' : 'Region detection failed, using defaults'}
                </div>
              )}

              <button
                onClick={handleRegionConfirm}
                disabled={regionLoading || !selectedPaymentMethod}
                className="w-full mt-6 h-12 rounded-xl font-bold text-sm bg-brand-900 text-white hover:bg-brand-800 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {regionLoading
                  ? (language === 'zh' ? '检测中...' : 'Detecting...')
                  : (language === 'zh' ? '确认支付' : 'Confirm Payment')}
              </button>

              <button
                onClick={() => { setShowRegionModal(false); setPendingPurchase(null); setSelectedPaymentMethod(null); }}
                className="w-full mt-3 py-3 rounded-xl text-sm font-bold text-ink-secondary bg-white border border-surface-border hover:bg-surface transition-all"
              >
                {language === 'zh' ? '取消' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 二维码支付弹窗 */}
      {showQrModal && qrCodeUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowQrModal(false)}
          ></div>
          <div className="relative w-full max-w-sm bg-surface-light rounded-modal shadow-2xl border border-surface-border overflow-hidden animate-in zoom-in-95 duration-300">
            <button
              onClick={() => setShowQrModal(false)}
              className="absolute top-6 right-6 z-20 p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>

            <div className="p-10 text-center">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg text-2xl font-bold ${
                qrPaymentType?.includes('wechat') ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
              }`}>
                {qrPaymentType?.includes('wechat') ? '微' : '支'}
              </div>

              <h3 className="text-xl font-serif font-bold text-brand-900 mb-2">
                {qrPaymentType?.includes('wechat')
                  ? (language === 'zh' ? '微信扫码支付' : 'WeChat Pay')
                  : (language === 'zh' ? '支付宝扫码支付' : 'Alipay')}
              </h3>

              <p className="text-xs text-ink-secondary mb-6">
                {language === 'zh' ? '请使用手机扫描下方二维码完成支付' : 'Scan the QR code below with your phone to pay'}
              </p>

              <div className="bg-white border-2 border-surface-border rounded-2xl p-4 inline-block mb-4">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeUrl)}`}
                  alt="Payment QR Code"
                  className="w-[200px] h-[200px]"
                />
              </div>

              {qrOrderNo && (
                <p className="text-xs text-ink-muted mb-4">
                  {language === 'zh' ? '订单号：' : 'Order: '}{qrOrderNo}
                </p>
              )}

              <p className="text-xs text-ink-secondary">
                {language === 'zh' ? '支付完成后页面将自动更新' : 'Page will update after payment'}
              </p>

              <button
                onClick={async () => {
                  setShowQrModal(false);
                  setQrCodeUrl(null);
                  await loadMembershipInfo();
                }}
                className="w-full mt-6 h-12 rounded-xl font-bold text-sm bg-brand-900 text-white hover:bg-brand-800 transition-all active:scale-[0.98]"
              >
                {language === 'zh' ? '我已完成支付' : 'I have paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ease-spring {
            transition-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        @keyframes swing {
            0% { transform: translateX(-50%) rotate(-5deg); }
            50% { transform: translateX(-50%) rotate(4deg); }
            100% { transform: translateX(-50%) rotate(-5deg); }
        }
      `}</style>
    </div>
  );
}
